import {exhaustive} from '@freckle/exhaustive'
import {fromJust} from '@freckle/maybe'
import type {ParserT} from '@freckle/parser'
import {stringEnum} from '@freckle/parser'
import {PATHS} from '@freckle/educator-entities/ts/common/helpers/paths'
import type {ProductPathsT} from '../products'

export type SubjectT = 'ela' | 'math' | 'science' | 'social_studies'

export const Subjects = {
  ela: 'ela' as SubjectT,
  math: 'math' as SubjectT,
  science: 'science' as SubjectT,
  socialStudies: 'social_studies' as SubjectT,

  all(): Array<SubjectT> {
    return [Subjects.math, Subjects.ela, Subjects.socialStudies, Subjects.science]
  },

  parse(textOrPath: string): SubjectT | undefined {
    const text = textOrPath.replace('-', '_')
    return Subjects.all().find(value => value === text)
  },

  parseThrows(text: string): SubjectT {
    return fromJust(Subjects.parse(text), `Invalid subject ${text}`)
  },

  // Note: Do not use when interpolating a subject with other localized text
  i18nKey(subject: SubjectT): string {
    switch (subject) {
      case 'ela':
        return 'SUBJECT_ELA'
      case 'math':
        return 'SUBJECT_MATH'
      case 'science':
        return 'SUBJECT_SCIENCE'
      case 'social_studies':
        return 'SUBJECT_SOCIAL_STUDIES'
      default:
        return exhaustive(subject)
    }
  },

  // Deprecated, use `select` in locize instead
  forDisplay(subject: SubjectT): string {
    switch (subject) {
      case 'ela':
        return 'ELA'

      case 'math':
        return 'Math'

      case 'science':
        return 'Science'

      case 'social_studies':
        return 'Social Studies'

      default:
        return exhaustive(subject, 'SubjectT')
    }
  },

  descriptiveName(subject: SubjectT): string {
    switch (subject) {
      case 'ela':
        return 'English (ELA)'

      case 'math':
      case 'science':
      case 'social_studies':
        return Subjects.forDisplay(subject)

      default:
        return exhaustive(subject, 'SubjectT')
    }
  },

  iconUrl(subject: SubjectT): string {
    switch (subject) {
      case 'ela':
        return `${PATHS.imageAssetsUrl}/icons/subjects/ela.svg`
      case 'math':
        return `${PATHS.imageAssetsUrl}/icons/subjects/math.svg`
      case 'science':
        return `${PATHS.imageAssetsUrl}/icons/subjects/science.svg`
      case 'social_studies':
        return `${PATHS.imageAssetsUrl}/icons/subjects/social-studies.svg`

      default:
        return exhaustive(subject, 'SubjectT')
    }
  },

  asContentArea(subject: SubjectT): ProductPathsT['subjectPath'] {
    switch (subject) {
      case 'social_studies':
        return 'social-studies'
      default:
        return subject
    }
  },
}

export const parseSubject: ParserT<SubjectT> = stringEnum('SubjectT', Subjects.parse)
